import React, { useEffect, useCallback, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import { useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import { Icon } from "@/components/atoms/Icon";
import { ProductSmallCard } from "@/components/molecules";
import type { PreorderFormProps } from "./PreorderForm.types";
import { PreorderCartItem } from "./molecules/PreorderCartItem";
import { CommentSection } from "./molecules/CommentSection";
import { useCart } from "@/hooks/useCart";
import { useCompletePreorder } from "@/hooks/useCompletePreorder";
import { useCartImageUrls } from "@/core/orders/images";
import { remainingCartQuantity } from "@/core/orders/quantity-limits";
import { itemKey } from "@/core/options/fingerprint";
import { useAppSelector } from "@/core/store";
import {
  selectBrandId,
  selectPointId,
  selectMaxPreorderAmount,
  selectIsReadOnly,
} from "@/core/app/selectors";
import { useMenu, usePrice } from "@/hooks";

const PreorderForm: React.FC<PreorderFormProps> = ({
  onEditItem,
  onAddMore,
  onItemSelect,
  initialComment = "",
  externalLoading = false,
  onClose,
}) => {
  const theme = useTheme();
  const price = usePrice();

  // Get maxPreorderAmount from Redux store (set by DataLoader from URL)
  const maxPreorderAmount = useAppSelector(selectMaxPreorderAmount);
  const isReadOnly = useAppSelector(selectIsReadOnly);

  // Use cart hook for all cart operations
  const {
    cartItems,
    totalSum,
    comment,
    loading: cartLoading,
    error: _cartError,
    updateQuantity,
    updateComment,
    clearCart,
    isEmpty,
    isValid,
  } = useCart();

  // Кнопка не создаёт заказ: черновик уже завела фоновая синхронизация.
  const { complete } = useCompletePreorder();

  // Картинки позиций: в снимке заказа лежит imageId, адрес читается отдельно.
  const cartImageUrls = useCartImageUrls();

  // Get brandId and pointId from app state
  const brandId = useAppSelector(selectBrandId);
  const pointId = useAppSelector(selectPointId);

  // Order creation state
  const [sending, setSending] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [_orderCreationError, setOrderCreationError] = useState<string | null>(
    null,
  );

  // Determine overall loading state
  const loading = cartLoading || externalLoading || sending;

  const { products: allMenuProducts } = useMenu();

  const recommendedItems = useMemo(() => {
    if (cartItems.length === 0) return allMenuProducts;

    const cartProductIds = new Set<string>();
    cartItems.forEach((item) => {
      if (item.productSnapshot?.productId) {
        cartProductIds.add(item.productSnapshot.productId);
      }
      if (item.productId) {
        cartProductIds.add(item.productId);
      }
    });

    return allMenuProducts.filter((product) => {
      if (cartProductIds.has(product.id)) return false;

      if (product.variants && product.variants.length > 0) {
        const allVariantsInCart = product.variants.every((v) =>
          cartProductIds.has(v.id),
        );
        if (allVariantsInCart) return false;
      }

      return true;
    });
  }, [allMenuProducts, cartItems]);

  // Вычисляем превышение лимита
  const isLimitExceeded = useMemo(() => {
    if (!maxPreorderAmount || maxPreorderAmount <= 0) return false;
    return totalSum > maxPreorderAmount;
  }, [totalSum, maxPreorderAmount]);

  const exceededAmount = useMemo(() => {
    if (!isLimitExceeded || !maxPreorderAmount) return 0;
    return totalSum - maxPreorderAmount;
  }, [totalSum, maxPreorderAmount, isLimitExceeded]);

  const exceededAmountFormatted = useMemo(() => {
    if (!exceededAmount) return "";
    return price.formatSimple(exceededAmount);
  }, [exceededAmount, price]);

  // Initialize comment if provided
  useEffect(() => {
    if (initialComment && !comment) {
      updateComment(initialComment);
    }
  }, [initialComment, comment, updateComment]);

  const handleCommentChange = useCallback(
    (value: string) => {
      updateComment(value);
    },
    [updateComment],
  );

  const handleQuantityChange = useCallback(
    (itemKey: string, quantity: number) => {
      updateQuantity(itemKey, quantity);
    },
    [updateQuantity],
  );

  const handleClear = useCallback(() => {
    clearCart();
    onClose?.();
  }, [clearCart, onClose]);

  const handleEditItem = useCallback(
    (itemId: string) => {
      onEditItem(itemId);
    },
    [onEditItem],
  );

  const handleCreateOrder = useCallback(async () => {
    // Clear previous errors and success state
    setOrderCreationError(null);
    setOrderSuccess(false);

    // Validate required parameters
    if (!brandId || !pointId) {
      setOrderCreationError(
        "Не указаны обязательные параметры ресторана. Обновите страницу.",
      );
      return;
    }

    // Validate cart has items
    if (isEmpty || !isValid) {
      setOrderCreationError(
        "Корзина пуста или недействительна. Добавьте позиции в заказ.",
      );
      return;
    }

    // Флаш незавершённого дебаунса, ожидание ответа и PREORDER_COMPLETED —
    // всё внутри complete(); на ошибке он же шлёт PREORDER_ERROR.
    setSending(true);
    try {
      const sent = await complete();
      if (sent) {
        setOrderSuccess(true);
      } else {
        setOrderCreationError(
          "Не удалось отправить заказ. Попробуйте ещё раз.",
        );
      }
    } finally {
      setSending(false);
    }
  }, [brandId, pointId, isEmpty, isValid, complete]);

  // Check if order creation is available
  const canCreateOrder =
    brandId && pointId && !isEmpty && isValid && !loading && !isLimitExceeded;

  // Ключ и адрес строки — itemKey(item), а не productId: у одного товара
  // теперь бывает несколько строк с разными наборами опций. С productId
  // ключи двух таких строк совпадали бы (React их путает), «+» на второй
  // менял бы количество первой, а обнуление второй сносило бы обе.
  const memoizedCartItems = useMemo(() => {
    return cartItems.map((item) => {
      const key = itemKey(item);

      return (
        <PreorderCartItem
          key={key}
          item={item}
          imageUrl={
            item.productSnapshot.imageId
              ? cartImageUrls[item.productSnapshot.imageId]
              : null
          }
          onQuantityChange={(quantity) => handleQuantityChange(key, quantity)}
          // onEditItem={() => handleEditItem(key)}
          disabled={loading || isReadOnly}
          maxQuantity={remainingCartQuantity(cartItems, key)}
        />
      );
    });
  }, [
    cartItems,
    cartImageUrls,
    handleQuantityChange,
    handleEditItem,
    loading,
    isReadOnly,
  ]);

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        maxWidth: "100%",
        position: "absolute",
        inset: 0,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          pt: 1,
          pl: 3,
          pr: 1,
          pb: 1,
          position: "sticky",
          top: 0,
          zIndex: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h2"
          sx={{
            fontSize: 26,
            lineHeight: 1.1,
            pt: 1,
          }}
        >
          Ваш предзаказ
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          position: "relative",
        }}
      >
        <Box px={3} flex={1}>
          {/* Cart Items Section */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {/* Cart Items List */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                paddingTop: 4,
                gap: 1.5,
              }}
            >
              {memoizedCartItems}
            </Box>

            {/* Action Buttons — hidden in readOnly mode */}
            {!isReadOnly && (
              <Box
                sx={{
                  display: "flex",
                  gap: 3.75,
                  alignItems: "center",
                }}
              >
                {/* Add More Button */}
                <Button
                  onClick={onAddMore}
                  disabled={loading}
                  startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                  variant="outlined"
                  sx={{
                    flex: 1,
                    height: 40,
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: "transparent",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "14px",
                    color: theme.palette.text.primary,
                    boxShadow: `0px 1px 2px 0px ${theme.palette.action.hover}`,
                    "&:hover": {
                      borderColor: theme.palette.action.selected,
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  Добавить ещё
                </Button>

                {/* Clear Button */}
                <Button
                  onClick={handleClear}
                  disabled={loading}
                  startIcon={<Icon name="trash04" size={18} />}
                  sx={{
                    textTransform: "none",
                    color: theme.palette.text.secondary,
                    minWidth: "auto",
                    fontSize: 14,
                    height: 42,
                  }}
                >
                  Очистить
                </Button>
              </Box>
            )}

            {/* Comment Section — hidden in readOnly mode */}
            {!isReadOnly && (
              <CommentSection
                value={comment}
                onChange={handleCommentChange}
                disabled={loading}
              />
            )}

            {/* Order Creation Section */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* Success Message */}
              {orderSuccess && (
                <Alert severity="success" sx={{ borderRadius: 2 }}>
                  Заказ успешно создан! Корзина будет очищена через несколько
                  секунд.
                </Alert>
              )}

              {/* Limit Exceeded Error */}
              {isLimitExceeded && (
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  <Typography component="div" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Превышен лимит депозита на {exceededAmountFormatted}
                  </Typography>
                  <Typography component="div" variant="body2">
                    Удалите часть позиций
                  </Typography>
                </Alert>
              )}

              {/* Other Error Messages (закомментировано, можно оставить для будущего использования) */}
              {/* {(orderCreationError || cartError || orderError) && (
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  {orderCreationError || cartError || orderError}
                </Alert>
              )} */}

              {/* Order Creation Help Text */}
              {isEmpty && (
                <Typography
                  variant="caption"
                  sx={{
                    textAlign: "center",
                    color: "secondary",
                    fontSize: "12px",
                  }}
                >
                  Добавьте позиции в корзину для создания заказа
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {/* Recommended Items Section — hidden in readOnly mode or when empty */}
        {!isReadOnly && recommendedItems.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              py: 2,
            }}
          >
            {/* Section Header */}
            <Typography
              variant="h5"
              component="h2"
              sx={{
                fontSize: "20px",
                mb: 0.5,
                ml: 3,
                lineHeight: 1.1,
              }}
            >
              Добавьте ещё
            </Typography>

            {/* Recommended Items Grid */}
            <Box sx={{ overflow: "hidden", width: "100%" }}>
              <Box
                sx={{
                  pt: 1,
                  display: "flex",
                  gap: 1,
                  overflowX: "auto",
                  paddingBottom: 1,
                  px: 3,
                  "&::-webkit-scrollbar": {
                    height: 4,
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: theme.palette.action.hover,
                    borderRadius: 2,
                    margin: theme.spacing(0, 3),
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: theme.palette.action.selected,
                    borderRadius: 2,
                  },
                }}
              >
                {recommendedItems.map((item) => (
                  <ProductSmallCard
                    key={item.id}
                    item={item}
                    onSelect={() => onItemSelect(item.id)}
                    disabled={loading}
                    maxWidth={120}
                    minWidth={100}
                    showPrice={true}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
      <Box
        sx={{
          p: 2,
          backgroundColor: "background.paper",
          boxShadow: `0px 0px 34px 0px rgba(0, 0, 0, 0.25);`,
          borderRadius: "24px 24px 0 0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          position: "relative",
          zIndex: 1,
        }}
      >
        <Box>
          <Typography fontSize={14}>{cartItems.length} позиции</Typography>
          <Typography fontSize={20} fontWeight={600}>
            {price.formatSimple(totalSum)}
          </Typography>
        </Box>
        {/* Create Order Button — hidden in readOnly mode */}
        {!isReadOnly && (
          <Button
            onClick={handleCreateOrder}
            disabled={!canCreateOrder}
            startIcon={<Icon name="checkCircleBroken" size={18} />}
            variant="contained"
            sx={{
              height: 50,
              borderRadius: 3,
              textTransform: "none",
              fontWeight: 600,
              fontSize: 16,
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              boxShadow: `0px 2px 4px 0px ${theme.palette.primary.main}30`,
              "&:hover": {
                backgroundColor: theme.palette.primary.dark,
                boxShadow: `0px 4px 8px 0px ${theme.palette.primary.main}40`,
              },
              "&:disabled": {
                backgroundColor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
                boxShadow: "none",
              },
            }}
          >
            {loading ? "Сохранение заказа..." : "К брони и оплате"}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export { PreorderForm };
